/**
 * @fileoverview PageHeader component - Layout component
 * 
 * A consistent page header component with title, description, and actions.
 * Used across all pages for consistent header styling.
 * 
 * @module ui/shared/layout/PageHeader
 */

import * as React from "react";
import { cn } from "../utils/cn";

/**
 * PageHeader component props
 */
export interface PageHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Page title
   */
  title: string;
  /**
   * Optional page description
   */
  description?: string;
  /**
   * Optional action buttons (rendered on the right)
   */
  actions?: React.ReactNode;
}

/**
 * PageHeader component
 * 
 * A consistent page header with title, description, and optional actions.
 * 
 * @param {PageHeaderProps} props - PageHeader props
 * @returns {JSX.Element} PageHeader element
 * 
 * @example
 * <PageHeader
 *   title="My Bookings"
 *   description="View and manage your reservations"
 *   actions={<Button>New Booking</Button>}
 * />
 */
const PageHeader = React.forwardRef<HTMLDivElement, PageHeaderProps>(
  ({ className, title, description, actions, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("mb-6 flex items-center justify-between", className)}
        {...props}
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{title}</h1>
          {description && (
            <p className="mt-1 text-sm text-gray-500">{description}</p>
          )}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
    );
  }
);

PageHeader.displayName = "PageHeader";

export { PageHeader };

